/**
 * Per-route SEO copy, in one place.
 *
 * These values are needed twice: by <SEOHead> when React renders the page in
 * the browser, and by scripts/prerender.mjs when the build writes the static
 * HTML that crawlers and social scrapers actually read. Keeping them in one
 * module is what stops the two from drifting — a title changed in a page
 * component but not in the prerendered HTML would be invisible in development
 * and wrong in every share card.
 *
 * Blog posts and service pages derive their SEO from their own records in
 * mock.js and are not repeated here.
 */

export const SITE_URL = 'https://dependifyllc.com.ng';
export const SITE_NAME = 'Dependify LLC Nigeria';

export const DEFAULT_TITLE = `${SITE_NAME} - AI Receptionists, WhatsApp Automation & AI Employees for Nigerian Businesses`;

export const DEFAULT_DESCRIPTION =
  'AI receptionists, AI employees and WhatsApp automation that answer every call and message in seconds, book appointments and follow up automatically — for Nigerian businesses in Lagos, Abuja, Port Harcourt and beyond.';

export const DEFAULT_KEYWORDS =
  'AI receptionist Nigeria, WhatsApp automation Nigeria, AI employees Nigeria, shortlet automation Lagos, appointment setting Nigeria, automated follow-up Nigeria, reminders Nigeria, AI chatbot Lagos, Dependify';

export const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;

/**
 * Sitemap fields for the fixed pages.
 *
 * `lastmod` is a claim about when the page's content last changed, so it is
 * stated per page rather than stamped with the build date — a sitemap where
 * every URL changes on every deploy tells a crawler nothing. Blog posts do not
 * appear here: theirs comes from each post's own modifiedDate, which is the
 * value that actually moves when an article is edited.
 */
export const PAGE_SITEMAP = {
  '/': { lastmod: '2026-09-08', changefreq: 'weekly', priority: '1.0' },
  '/services': { lastmod: '2026-09-02', changefreq: 'weekly', priority: '0.9' },
  '/how-it-works': { lastmod: '2026-09-03', changefreq: 'monthly', priority: '0.9' },
  '/about': { lastmod: '2026-09-02', changefreq: 'monthly', priority: '0.8' },
  '/contact': { lastmod: '2026-09-02', changefreq: 'monthly', priority: '0.9' },
  '/blog': { lastmod: '2026-09-08', changefreq: 'daily', priority: '0.9' },
};

/** Service detail pages all carry the same cadence and weight. */
export const SERVICE_SITEMAP = { lastmod: '2026-09-02', changefreq: 'monthly', priority: '0.8' };

/** Blog posts: weight and cadence fixed, lastmod taken from the post. */
export const BLOG_SITEMAP = { changefreq: 'monthly', priority: '0.7' };

export const PAGE_SEO = {
  '/': {
    title: 'AI Receptionists, WhatsApp Automation & Shortlet Growth for Nigerian Businesses',
    description:
      'Dependify builds AI receptionists, AI employees, WhatsApp Business automation, appointment setting, follow-up systems, reminders & confirmations for Nigerian businesses — shortlets, clinics, law firms and more in Lagos, Abuja & Port Harcourt. Instant replies, 24/7.',
    keywords:
      'AI receptionist Nigeria, WhatsApp automation Nigeria, AI employees Nigeria, shortlet automation Lagos, appointment setting service Nigeria, automated follow-up Nigeria, WhatsApp reminders Nigeria, AI chatbot for Nigerian business, chatbot Lagos, Dependify',
  },
  '/services': {
    title: 'AI Automation Services for Nigerian Businesses',
    description:
      'AI receptionists, AI employees, WhatsApp Business automation, appointment setting, follow-up systems, reminders & confirmations, and our Shortlet Growth Suite — for businesses in Lagos, Abuja, Port Harcourt and across Nigeria.',
    keywords:
      'AI receptionist Nigeria, WhatsApp automation Nigeria, AI employees Nigeria, shortlet automation Lagos, appointment setting Nigeria, reminder messages Nigeria, AI chatbot Lagos, chatbot for Nigerian business, Dependify services',
  },
  '/how-it-works': {
    title: 'How It Works & Pricing - No Facebook Account Needed',
    description:
      'Set up AI WhatsApp automation for your Nigerian business without a Facebook account. We handle the entire Meta setup. Transparent setup fees and monthly pricing for all twelve services.',
    keywords:
      'WhatsApp automation setup Nigeria, WhatsApp API without Facebook account, AI receptionist pricing Nigeria, shortlet automation pricing, WhatsApp Business API Nigeria cost, Dependify pricing',
  },
  '/about': {
    title: "About Us - Nigeria's AI Automation Experts",
    description:
      'Meet Tolulope Omotosho and the Dependify team — 15+ years of experience, now deploying AI receptionists, AI employees and WhatsApp automation for Nigerian businesses in Lagos, Abuja, Port Harcourt and beyond.',
    keywords:
      'Dependify Nigeria, AI automation agency Lagos, Tolulope Omotosho, AI receptionist Nigeria, WhatsApp automation Nigeria, about Dependify',
  },
  '/contact': {
    title: 'Contact Us - Free AI Automation Consultation',
    description:
      'Book a free consultation with Dependify LLC Nigeria. See a live demo of an AI receptionist or WhatsApp automation trained on your business — for shortlets, clinics, law firms and service businesses in Lagos, Abuja, Port Harcourt and beyond.',
    keywords:
      'contact Dependify, AI receptionist demo Nigeria, WhatsApp automation Lagos, shortlet automation Nigeria, free consultation AI Nigeria',
  },
  '/blog': {
    title: 'AI & Automation Blog for Nigerian Businesses',
    description:
      'Practical guides on AI receptionists, WhatsApp Business automation, shortlet booking systems, appointment reminders and AI employees — written for Nigerian businesses in Lagos, Abuja, Port Harcourt and beyond.',
    keywords:
      'AI automation Nigeria blog, WhatsApp automation guide Nigeria, shortlet automation Lagos, AI receptionist Nigeria, appointment reminders Nigeria, AI employees guide',
  },
};

/** SEO for a service detail page, derived from the service record. */
export function serviceSeo(service) {
  return {
    title: service.title,
    description: service.shortDesc,
    keywords: `${service.title}, ${service.seoTag}, ${service.features.slice(0, 3).join(', ')}, Nigeria, Lagos, Abuja, Port Harcourt`,
    image: service.image,
  };
}

/** SEO for a blog post page, derived from the post record. */
export function blogPostSeo(post) {
  return {
    title: post.title,
    description: post.excerpt,
    keywords: post.tags.join(', '),
    image: post.image,
    type: 'article',
    author: post.author?.name,
    publishedTime: post.publishedDate,
    modifiedTime: post.modifiedDate,
    section: post.category,
    tags: post.tags,
  };
}

/**
 * The exact title <SEOHead> renders, so the prerendered <title> and the one
 * React swaps in on hydration are the same string.
 */
export function fullTitle(title) {
  return title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
}
